/**
 * Joins non-empty parts with `separator`, dropping case-insensitive
 * duplicates while keeping the casing of the first occurrence (FLOSEAI-599).
 * A location scraped as `{ city: 'Colombia', country: 'Colombia' }` renders
 * as "Colombia" instead of "Colombia, Colombia".
 */
export function joinUniqueParts(parts: Array<string | null | undefined>, separator = ', '): string {
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const part of parts) {
    if (!part) continue;
    const trimmed = part.trim();
    if (trimmed.length === 0) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(trimmed);
  }

  return unique.join(separator);
}

export function formatCurrency(amount: number, currency = 'USD', locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatNumber(value: number, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function formatDate(
  date: string | Date,
  locale = 'en-US',
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(
    locale,
    options ?? {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
  );
}

export function formatDateTime(date: string | Date, locale = 'en-US'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const PHONE_FORMATS: Record<string, { code: string; pattern: (digits: string) => string }> = {
  US: {
    code: '+1',
    pattern: (d) => (d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d),
  },
  BR: {
    code: '+55',
    pattern: (d) => {
      if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
      if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return d;
    },
  },
  MX: {
    code: '+52',
    pattern: (d) => (d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d),
  },
  CO: {
    code: '+57',
    pattern: (d) => (d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : d),
  },
  AR: {
    code: '+54',
    pattern: (d) => (d.length === 10 ? `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}` : d),
  },
};

export function formatPhoneNumber(phone: string, country = 'US'): string {
  const digits = phone.replaceAll(/\D/g, '');
  const format = PHONE_FORMATS[country];

  if (!format) {
    return phone;
  }

  let localDigits = digits;
  if (digits.startsWith(format.code.replace('+', ''))) {
    localDigits = digits.slice(format.code.length - 1);
  }

  return `${format.code} ${format.pattern(localDigits)}`;
}
